import { CommonModule } from '@angular/common';
import {
  Component,
  AfterViewInit,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgSelectModule } from '@ng-select/ng-select';
import { ActivatedRoute, Router } from '@angular/router';
import { ResponsiveLayoutService } from '../shared-services/responsive-layout.service';
import { DashboardTitleService } from '../shared-services/dashboard-title.service';
import { SelfServiceChartService } from './shared/self-service-chart.service';
import { SelfServiceStateService } from './shared/self-service-state.service';
import { SelfServiceUiService } from './shared/self-service-ui.service';
import { SelfServiceChartPersistenceService } from './shared/self-service-chart-persistence.service';
import { readJson } from './shared/self-service-storage.utils';
import { SelfServiceChatComponent } from './self-service-chat.component';
import { ChartBuilderComponent } from './chart-builder/chart-builder.component';
import { DataBuilderComponent } from './data-builder/data-builder.component';
import { DashboardBuilderComponent } from './dashboard-builder/dashboard-builder.component';
import { RuleBuilderComponent } from './rule-builder/rule-builder.component';
import { DvmLayoutBuilderComponent } from './dvm-layout-builder/dvm-layout-builder.component';
import { DesignBuilderComponent } from './design-builder/design-builder.component';
import { KaveriGenerateBuilderComponent } from './kaveri-generate-builder/kaveri-generate-builder.component';
import { WhatsNewComponent } from './whats-new/whats-new.component';
import { CURRENT_VERSION } from './shared/release-notes.data';
import { openUserGuide } from '../utils/user-guide.utils';

import {
  ChartTypeOption,
  MappingKey,
  SavedSelfServiceChart,
  UploadedDataset,
} from "./shared/self-service-types";

/** The builder stages the shell can show. Also the `?view=` query-param values. */
export type SelfServiceViewMode =
  | 'data'
  | 'charts'
  | 'dashboards'
  | 'rules'
  | 'layout'
  | 'design'
  | 'generate';

@Component({
  selector: 'app-self-service-visualization',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, MatSnackBarModule, MatDialogModule, NgSelectModule, SelfServiceChatComponent, ChartBuilderComponent, DataBuilderComponent, DashboardBuilderComponent, RuleBuilderComponent, DvmLayoutBuilderComponent, DesignBuilderComponent, KaveriGenerateBuilderComponent, WhatsNewComponent],
  templateUrl: './self-service-visualization.component.html',
  styleUrl: './self-service-visualization.component.scss',
})
export class SelfServiceVisualizationComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly chartService = inject(SelfServiceChartService);
  // Shared cross-stage state owned by the sub-components; the shell only seeds
  // the bootstrap signals (chart types + saved charts) and reads a few for the
  // header chips.
  protected readonly state = inject(SelfServiceStateService);
  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly dashboardTitleService = inject(DashboardTitleService);
  private chatDialogRef: MatDialogRef<unknown> | null = null;
  private whatsNewDialogRef: MatDialogRef<unknown> | null = null;

  private static readonly LAST_SEEN_VERSION_STORAGE_KEY = 'dvm.selfService.lastSeenReleaseVersion';
  readonly currentVersion = CURRENT_VERSION;
  hasUnseenRelease = false;

  @ViewChild('chatDialogTemplate') chatDialogTemplate: any;
  @ViewChild('whatsNewDialogTemplate') whatsNewDialogTemplate: any;
  @ViewChild(ChartBuilderComponent) private chartBuilder?: ChartBuilderComponent;
  @ViewChild(DashboardBuilderComponent) private dashboardBuilder?: DashboardBuilderComponent;

  private static readonly SAVED_CHARTS_STORAGE_KEY = 'dvm.selfService.savedCharts';

  // Shared signal (read by chart-builder picker + dashboard previewKind/label lookups).
  get chartTypes(): ChartTypeOption[] { return this.state.chartTypes(); }
  set chartTypes(value: ChartTypeOption[]) { this.state.setChartTypes(value); }

  // Backed by the shared signal so chart-builder reacts to dataset changes.
  get dataset(): UploadedDataset | undefined { return this.state.dataset(); }

  get savedCharts(): SavedSelfServiceChart[] { return this.state.savedCharts(); }
  set savedCharts(v: SavedSelfServiceChart[]) { this.state.setSavedCharts(v); }
  get dashboardName(): string { return this.state.dashboardName(); }
  get dashboardItems() { return this.state.dashboardItems(); }

  // Stale-but-bound entity-preview popup state. The data-builder owns the live
  // entity flow (it has its own popup); these keep the shell's popup template
  // and chat column lookup compiling.
  selectedEntity: string | null = null;
  entityTableRows: Array<Record<string, unknown>> = [];
  entityTableColumns: string[] = [];
  isEntityPreviewOpen = false;
  isSelfServiceChatOpen = false;
  chatMissingFieldSelections: Record<string, string> = {};

  viewMode: SelfServiceViewMode = 'data';
  navigationOpen = false;
  launcherPosition: { x: number; y: number } | null = null;
  launcherDragging = false;
  private launcherDragStart: { pointerX: number; pointerY: number; x: number; y: number } | null = null;
  private launcherMoved = false;
  private static readonly LAUNCHER_POSITION_KEY = 'dvm.selfService.launcherPosition';
  private static readonly LAUNCHER_DEBUG_KEY = '[DVM_ORBIT_DEBUG_V1]';
  readonly navigationItems: Array<{
    mode: SelfServiceViewMode;
    label: string;
    icon: string;
  }> = [
    { mode: 'data', label: 'Data Builder', icon: 'storage' },
    { mode: 'rules', label: 'Rule Builder', icon: 'rule' },
    { mode: 'charts', label: 'Chart Builder', icon: 'insert_chart' },
    { mode: 'dashboards', label: 'Dashboard Builder', icon: 'dashboard_customize' },
    { mode: 'layout', label: 'DVM Layout Builder', icon: 'view_quilt' },
    { mode: 'design', label: 'Design Builder', icon: 'design_services' },
    { mode: 'generate', label: 'Kaveri Generation', icon: 'build' },
  ];

  get currentNavigationItem() {
    return this.navigationItems.find((item) => item.mode === this.viewMode) || this.navigationItems[0];
  }

  get availableNavigationItems() {
    return this.navigationItems.filter((item) => item.mode !== this.viewMode);
  }

  private get topNavbarTitle(): string {
    switch (this.viewMode) {
      case 'dashboards': return 'Dashboard Builder';
      case 'charts': return 'Chart Builder';
      case 'layout': return 'DVM Layout Builder';
      case 'design': return 'Layout Designer';
      case 'generate': return 'Kaveri Generation';
      case 'rules': return 'Rule Builder';
      case 'data': return 'Data Builder';
    }
  }

  private updateTopNavbarTitle(): void {
    this.dashboardTitleService.setDashboardTitle(this.topNavbarTitle, 'top');
  }

  toggleScreenNavigation(): void {
    if (this.launcherMoved) {
      this.launcherMoved = false;
      return;
    }
    this.navigationOpen = !this.navigationOpen;
  }

  startLauncherDrag(event: PointerEvent): void {
    if ((event.target as HTMLElement).closest('.orbit-screen-button')) return;
    const nav = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.launcherDragStart = { pointerX: event.clientX, pointerY: event.clientY, x: nav.left, y: nav.top };
    this.launcherDragging = false;
    this.launcherMoved = false;
  }

  @HostListener('window:pointermove', ['$event'])
  moveLauncher(event: PointerEvent): void {
    if (!this.launcherDragStart) return;
    const dx = event.clientX - this.launcherDragStart.pointerX;
    const dy = event.clientY - this.launcherDragStart.pointerY;
    if (!this.launcherMoved && Math.hypot(dx, dy) < 12) return;
    this.launcherMoved = true;
    this.launcherDragging = true;
    this.launcherPosition = this.clampLauncherPosition({
      x: this.launcherDragStart.x + dx,
      y: this.launcherDragStart.y + dy,
    });
  }

  @HostListener('window:pointerup')
  stopLauncherDrag(): void {
    if (!this.launcherDragStart) return;
    this.launcherDragStart = null;
    this.launcherDragging = false;
    if (this.launcherPosition) {
      try { localStorage.setItem(SelfServiceVisualizationComponent.LAUNCHER_POSITION_KEY, JSON.stringify(this.launcherPosition)); } catch { /* optional preference */ }
    }
    this.logLauncherDiagnostics('pointer-up');
  }

  @HostListener('window:pointercancel')
  cancelLauncherDrag(): void {
    this.stopLauncherDrag();
  }

  @HostListener('window:resize')
  keepLauncherOnScreen(): void {
    if (this.launcherPosition) this.launcherPosition = this.clampLauncherPosition(this.launcherPosition);
    setTimeout(() => this.logLauncherDiagnostics('window-resize'));
  }

  private clampLauncherPosition(position: { x: number; y: number }): { x: number; y: number } {
    const page = document.querySelector('.self-service-page')?.getBoundingClientRect();
    const minX = Math.max(8, (page?.left ?? 0) + 8);
    const minY = Math.max(8, (page?.top ?? 0) + 8);
    const maxX = Math.max(minX, Math.min(window.innerWidth - 60, (page?.right ?? window.innerWidth) - 60));
    const maxY = Math.max(minY, Math.min(window.innerHeight - 60, (page?.bottom ?? window.innerHeight) - 60));
    return {
      x: Math.min(maxX, Math.max(minX, position.x)),
      y: Math.min(maxY, Math.max(minY, position.y)),
    };
  }

  ngAfterViewInit(): void {
    // Layout bounds are final here; recover legacy/off-screen saved positions.
    if (this.launcherPosition) {
      this.launcherPosition = this.clampLauncherPosition(this.launcherPosition);
      try { localStorage.setItem(SelfServiceVisualizationComponent.LAUNCHER_POSITION_KEY, JSON.stringify(this.launcherPosition)); } catch { /* optional preference */ }
    }
    (window as unknown as Record<string, unknown>)['DVM_ORBIT_DEBUG_V1'] = () => this.logLauncherDiagnostics('manual-console-call');
    setTimeout(() => this.logLauncherDiagnostics('after-view-init'));
  }

  private logLauncherDiagnostics(reason: string): Record<string, unknown> {
    const launcher = document.querySelector<HTMLElement>('.screen-orbit-nav');
    const page = document.querySelector<HTMLElement>('.self-service-page');
    const rect = launcher?.getBoundingClientRect();
    const style = launcher ? getComputedStyle(launcher) : null;
    const ancestors: Array<Record<string, string>> = [];
    let ancestor = launcher?.parentElement || null;
    while (ancestor && ancestors.length < 12) {
      const ancestorStyle = getComputedStyle(ancestor);
      ancestors.push({
        element: `${ancestor.tagName.toLowerCase()}${ancestor.id ? '#' + ancestor.id : ''}${ancestor.className ? '.' + String(ancestor.className).trim().replace(/\s+/g, '.') : ''}`,
        display: ancestorStyle.display,
        visibility: ancestorStyle.visibility,
        opacity: ancestorStyle.opacity,
        overflow: `${ancestorStyle.overflowX}/${ancestorStyle.overflowY}`,
        position: ancestorStyle.position,
        zIndex: ancestorStyle.zIndex,
        transform: ancestorStyle.transform,
      });
      ancestor = ancestor.parentElement;
    }
    const diagnostic = {
      reason,
      timestamp: new Date().toISOString(),
      viewMode: this.viewMode,
      navigationOpen: this.navigationOpen,
      launcherDragging: this.launcherDragging,
      configuredPosition: this.launcherPosition,
      savedPosition: localStorage.getItem(SelfServiceVisualizationComponent.LAUNCHER_POSITION_KEY),
      viewport: { width: window.innerWidth, height: window.innerHeight, scrollX: window.scrollX, scrollY: window.scrollY },
      pageRect: page?.getBoundingClientRect().toJSON(),
      launcherFound: !!launcher,
      launcherRect: rect?.toJSON(),
      launcherInViewport: !!rect && rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight,
      computed: style ? {
        display: style.display,
        visibility: style.visibility,
        opacity: style.opacity,
        position: style.position,
        zIndex: style.zIndex,
        pointerEvents: style.pointerEvents,
        overflow: `${style.overflowX}/${style.overflowY}`,
        transform: style.transform,
      } : null,
      ancestors,
    };
    console.log(SelfServiceVisualizationComponent.LAUNCHER_DEBUG_KEY, diagnostic);
    return diagnostic;
  }

  navigateFromLauncher(mode: SelfServiceViewMode): void {
    this.setViewMode(mode);
    this.navigationOpen = false;
  }

  @HostListener('window:dvm:self-service-action', ['$event'])
  handleTopNavbarAction(event: CustomEvent<'whats-new' | 'help' | 'chat'>): void {
    if (event.detail === 'whats-new') this.openWhatsNew();
    if (event.detail === 'help') this.openHelp();
    if (event.detail === 'chat') this.openSelfServiceChat();
  }

  @HostListener('window:dvm:self-service-navigate', ['$event'])
  handleTopNavbarNavigation(event: CustomEvent<string>): void {
    const requestedMode = event.detail;
    if (this.navigationItems.some((item) => item.mode === requestedMode)) {
      this.setViewMode(requestedMode as SelfServiceViewMode);
    }
  }

  private _errorMessage = '';
  get errorMessage(): string { return this._errorMessage; }
  set errorMessage(val: string) {
    this._errorMessage = val;
    this.ui.showError(val);
  }

  get selfServiceChatColumns(): string[] {
    const columnNames = new Set<string>();
    this.entityTableColumns.forEach((column) => columnNames.add(column));
    (this.dataset?.columns || []).forEach((column) => columnNames.add(column.name));
    return Array.from(columnNames);
  }

  /** Chart routed from the dashboard's "edit" action, passed to ChartBuilder. */
  pendingEditChart: SavedSelfServiceChart | null = null;

  ngOnInit(): void {
    this.loadChartTypes();
    this.loadSavedCharts();
    this.applyInitialViewMode();
    this.updateTopNavbarTitle();
    this.checkForUnseenRelease();
    try {
      const saved = JSON.parse(localStorage.getItem(SelfServiceVisualizationComponent.LAUNCHER_POSITION_KEY) || 'null');
      if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) {
        this.launcherPosition = this.clampLauncherPosition(saved);
      }
    } catch { this.launcherPosition = null; }
  }

  private checkForUnseenRelease(): void {
    try {
      const lastSeen = window.localStorage.getItem(SelfServiceVisualizationComponent.LAST_SEEN_VERSION_STORAGE_KEY);
      this.hasUnseenRelease = lastSeen !== this.currentVersion;
    } catch {
      this.hasUnseenRelease = false;
    }
  }

  /** When the published dashboard view's "Back to Builder" navigates here it
   *  passes `view=dashboards` / `preview=true` (query params + nav state). Mount
   *  the dashboard-builder so its own `restoreDashboardBuilderReturnTarget()`
   *  can load the dashboard and land on the Arrange step. Without this the shell
   *  would stay on its default 'rules' view and the builder would never mount. */
  private applyInitialViewMode(): void {
    const navState = (this.router.getCurrentNavigation()?.extras.state || history.state || {}) as {
      selfServiceView?: string;
      preview?: boolean;
    };
    const query = this.route.snapshot.queryParamMap;
    const requestedView = navState.selfServiceView || query.get('view') || '';

    if (this.navigationItems.some((item) => item.mode === requestedView)) {
      this.setViewMode(requestedView as SelfServiceViewMode);
      return;
    }

    // DVM-layout dashboards return here via view=layout — mount the DVM builder
    // (it self-restores the dashboard from the ?dashboard slug). Check this
    // BEFORE the dashboards branch, since both carry preview=true.
    if (requestedView === 'layout') {
      this.setViewMode('layout');
      return;
    }

    const shouldOpenDashboard =
      requestedView === 'dashboards' || query.get('preview') === 'true' || !!navState.preview;

    if (shouldOpenDashboard) {
      this.setViewMode('dashboards');
    }
  }

  ngOnDestroy(): void {
    this.chatDialogRef?.close();
    this.whatsNewDialogRef?.close();
    this.dashboardTitleService.clearDashboardTitle();
    delete (window as unknown as Record<string, unknown>)['DVM_ORBIT_DEBUG_V1'];
  }

  private loadChartTypes(): void {
    // Future: replace with HTTP call to backend chart-type registry.
    const axisDefaults = {
      supportsSort: true,
      supportsTopN: true,
      supportsDateGranularity: true,
      supportsTooltipColumns: true,
      supportsReferenceLine: true,
    } as const;
    const dualAxisExtras = { optionalMappings: ['xAxis2', 'yAxis2'] as MappingKey[] };
    const scatterDefaults = { supportsTooltipColumns: true } as const;
    const scatterExtras = { optionalMappings: ['size', 'colorBy'] as MappingKey[] };
    const pieDefaults = { supportsSort: true, supportsTopN: true, supportsTooltipColumns: true } as const;

    this.chartTypes = [
      { label: 'Bar Chart', value: 'bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Horizontal Bar Chart', value: 'horizontal-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'horizontal-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Stack Bar Chart', value: 'stack-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Generic Stack Bar Chart', value: 'generic-stack-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Mixed Stack Bar Chart', value: 'mixed-stackbar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: '100% Stacked Bar Chart', value: 'stacked-100-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-horizontal-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Stacked Horizontal Bar Chart', value: 'stacked-horizontal-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-horizontal-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Horizontal Bar Chart Segment', value: 'horizontal-bar-chart-segment', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-horizontal-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Clustered Bar Chart', value: 'clustered-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Comparison Bar Chart', value: 'comparison-bar-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Grouped Bar Chart with Status Table', value: 'grouped-bar-chart-with-status-table', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Named Bar Chart with Division', value: 'named-bar-chart-with-division', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Waterfall Chart', value: 'waterfall-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'waterfall', supportsAggregation: true, ...axisDefaults },
      { label: 'Waterfall Stack Chart', value: 'waterfall-stack', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'waterfall-stack', supportsAggregation: true, ...axisDefaults },
      { label: 'Line Chart', value: 'line-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Step Line Chart', value: 'step-line-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'step-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Stacked Line Chart', value: 'stacked-line-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Stacked Area Chart', value: 'stacked-area-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'stacked-area', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Line Column Chart', value: 'line-column-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Multi-Level Comparison Chart', value: 'multi-level-comparison-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'axis-bar', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Multi-Series Bar-Line Combo Chart', value: 'multi-series-bar-line-combo-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Bar-Line Chart', value: 'bar-line', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Multi-Bar Multi-Line Chart', value: 'mbar-mline', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Combined Chart', value: 'combined-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bar-line', supportsAggregation: true, ...axisDefaults, ...dualAxisExtras },
      { label: 'Gantt Chart', value: 'gantt-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'gantt', supportsAggregation: true, ...axisDefaults },
      { label: 'Pie Chart', value: 'pie-chart', requiredMappings: ['category', 'value'], previewKind: 'pie', supportsAggregation: true, ...pieDefaults },
      { label: 'Donut Chart', value: 'donut-chart', requiredMappings: ['category', 'value'], previewKind: 'donut', supportsAggregation: true, ...pieDefaults },
      { label: 'Treemap Chart', value: 'treemap-chart', requiredMappings: ['category', 'value'], previewKind: 'treemap', supportsAggregation: true, ...pieDefaults },
      { label: 'Scatter Plot', value: 'scatter-plot', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'scatter', supportsAggregation: false, ...scatterDefaults, ...scatterExtras },
      { label: 'Bubble Chart', value: 'bubble-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bubble', supportsAggregation: false, ...scatterDefaults, ...scatterExtras },
      { label: 'Quadrant Bubble Chart', value: 'quad-bubble-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'quadrant-bubble', supportsAggregation: false, ...scatterDefaults, ...scatterExtras },
      { label: 'Configurable Multi-Series Scatter Chart', value: 'configurable-multi-series-scatter-chart', requiredMappings: ['xAxis', 'yAxis'], previewKind: 'bubble', supportsAggregation: false, ...scatterDefaults, optionalMappings: ['xAxis2', 'size', 'colorBy'] },
      { label: 'KPI Card', value: 'kpi-card', requiredMappings: ['value'], previewKind: 'kpi', supportsAggregation: true, supportsComparison: true, supportsTooltipColumns: true },
      { label: 'Data Table', value: 'data-table', requiredMappings: [], previewKind: 'table', supportsAggregation: false, supportsSort: true, supportsTopN: true, supportsTooltipColumns: true },
      // Generic drill-down (tree table): builds an expandable hierarchy from the
      // dataset's dimension columns (string) with the numeric columns summed at
      // each level. Like Data Table it needs no explicit mapping — the component
      // infers levels/measures from the chosen columns.
      { label: 'Drill-Down Table', value: 'generic-drill-down', requiredMappings: [], previewKind: 'generic-drill-down', supportsAggregation: false, supportsSort: false, supportsTopN: true },
      // ── Interactive widgets ────────────────────────────────────────────────
      // Data-less UI controls. They require no column mapping (requiredMappings:
      // []) and don't aggregate; their settings live in the saved chart's
      // `config`/`widgetConfig`. previewKind === value drives the widget render
      // path in the builder preview, dashboard cards and published view.
      { label: 'Input Box', value: 'input-box', requiredMappings: [], previewKind: 'input-box', supportsAggregation: false },
      { label: 'Slider', value: 'slider', requiredMappings: [], previewKind: 'slider', supportsAggregation: false },
      { label: 'Range Slider', value: 'slider-range', requiredMappings: [], previewKind: 'slider-range', supportsAggregation: false },
      { label: 'Range Bidirectional Slider', value: 'range-bidirectional-slider', requiredMappings: [], previewKind: 'range-bidirectional-slider', supportsAggregation: false },
      { label: 'Configurable Form', value: 'configurable-form', requiredMappings: [], previewKind: 'configurable-form', supportsAggregation: false },
      { label: 'Editable Table', value: 'editable-table', requiredMappings: [], previewKind: 'editable-table', supportsAggregation: false },
      { label: 'Button', value: 'button', requiredMappings: [], previewKind: 'button', supportsAggregation: false },
      { label: 'Notes', value: 'notes', requiredMappings: [], previewKind: 'notes', supportsAggregation: false },
      { label: 'Upload File', value: 'upload-file', requiredMappings: [], previewKind: 'upload-file', supportsAggregation: false },
    ];

    this.chartService.listChartTypes<ChartTypeOption>().subscribe({
      next: (remoteChartTypes) => {
        if (remoteChartTypes.length) {
          this.chartTypes = remoteChartTypes;
        }
      },
    });
  }

  private loadSavedCharts(): void {
    // readJson transparently handles the compressed (new) and legacy plaintext
    // (old) payloads written under this key.
    this.savedCharts = readJson<SavedSelfServiceChart[]>(
      SelfServiceVisualizationComponent.SAVED_CHARTS_STORAGE_KEY,
      [],
    );
  }

  openHelp(anchor?: string): void {
    // Opening from a "How to use this" link inside What's New — close that
    // panel first so the guide tab doesn't feel like it's stacking on top.
    if (anchor) {
      this.closeWhatsNew();
    }
    openUserGuide(anchor);
  }

  openSelfServiceChat(): void {
    if (this.chatDialogRef) return;
    this.chatDialogRef = this.dialog.open(this.chatDialogTemplate, {
      width: 'min(1180px, 96vw)',
      height: 'min(860px, calc(100dvh - 48px))',
      maxWidth: '96vw',
      panelClass: 'ss-chat-dialog-panel',
      backdropClass: 'ss-chat-dialog-backdrop',
      autoFocus: false,
      restoreFocus: false,
    });
    this.chatDialogRef.afterClosed().subscribe(() => {
      this.chatDialogRef = null;
      this.isSelfServiceChatOpen = false;
    });
    this.isSelfServiceChatOpen = true;
  }

  closeSelfServiceChat(): void {
    this.chatDialogRef?.close();
    this.chatDialogRef = null;
    this.isSelfServiceChatOpen = false;
  }

  openWhatsNew(): void {
    if (this.whatsNewDialogRef) return;
    this.whatsNewDialogRef = this.dialog.open(this.whatsNewDialogTemplate, {
      width: 'min(560px, 92vw)',
      maxHeight: '88vh',
      panelClass: 'whats-new-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
    });
    this.whatsNewDialogRef.afterClosed().subscribe(() => {
      this.whatsNewDialogRef = null;
    });

    // Mark the current version as seen the moment the panel is opened.
    this.hasUnseenRelease = false;
    try {
      window.localStorage.setItem(SelfServiceVisualizationComponent.LAST_SEEN_VERSION_STORAGE_KEY, this.currentVersion);
    } catch {
      // Best-effort only — badge will simply reappear next session.
    }
  }

  closeWhatsNew(): void {
    this.whatsNewDialogRef?.close();
    this.whatsNewDialogRef = null;
  }

  closeEntityPreview(): void {
    this.isEntityPreviewOpen = false;
  }

  onSelfServiceChatSelectionsChange(selections: Record<string, string>): void {
    this.chatMissingFieldSelections = selections;
  }

  setViewMode(mode: SelfServiceViewMode): void {
    this.viewMode = mode;
    this.updateTopNavbarTitle();
    this.navigationOpen = false;
    setTimeout(() => {
      this.responsiveLayoutService.triggerReflow();
      this.logLauncherDiagnostics(`view-mode:${mode}`);
      if (mode === 'dashboards') {
        this.dashboardBuilder?.refreshVisibleCharts();
      }
    }, 0);
  }

  /** Data-builder "Reset" cleared the dataset; clear shared chart state so the
   *  chart-builder starts fresh next time it mounts. */
  startNewChart(): void {
    if (this.chartBuilder) {
      this.chartBuilder.startNewChart();
      return;
    }
    this.state.setDataset(undefined);
    this.state.setRawDataset(undefined);
    this.state.setActiveSavedChartId('');
  }
  /** Dashboard repository "New Chart" should keep the uploaded data but open a
   *  blank chart flow instead of returning to the last edited chart step. */
  startNewChartFromDashboard(): void {
    this.pendingEditChart = null;
    this.setViewMode('charts');
    queueMicrotask(() => {
      this.chartBuilder?.startNewChart({ preserveDataset: true });
    });
  }

  /** ChartBuilder asked to add a chart to the dashboard draft. Mutate the shared
   *  dashboard-items signal + persist; the DashboardBuilder renders reactively. */
  onChartBuilderAddToDashboard(chart: SavedSelfServiceChart): void {
    const item = {
      id: this.createId(),
      chartId: chart.id,
      addedAt: new Date().toISOString(),
    };
    const next = [...this.state.dashboardItems(), item];
    this.state.setDashboardItems(next);
    this.persistence.persistDashboardDraft(next);
    // Arriving in the dashboard-builder from "Add to dashboard" should start the
    // user at Setup, not Arrange. The dashboard-builder consumes this on mount.
    this.state.setPendingDashboardStep('setup');
  }

  /** A saved chart changed — re-persisting affected saved dashboards is handled
   *  inside ChartBuilder's saveChart via the persistence service; nothing
   *  further needed in the shell. */
  onChartBuilderDashboardRefresh(): void {}

  /** Dashboard asked to edit a chart — route it to ChartBuilder and switch view. */
  onDashboardEditChart(chart: SavedSelfServiceChart): void {
    this.pendingEditChart = chart;
    this.setViewMode('charts');
  }

  private createId(): string {
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}



